import { useEffect, useState } from "react";

import { logger } from "@/telemetry";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";

/**
 * Minimal shape of a workflow needed to surface it as a selectable row in the
 * chat command menu. Mirrors the relevant fields of the admin `WorkflowOutput`.
 */
export interface WorkflowListItem {
  id: string;
  name: string;
  description?: string;
  icon?: string;
}

/**
 * Module-level cache so navigating between chats shows the previous list
 * immediately instead of flashing empty while the refetch runs.
 *
 * `cachedForClient` ties the cache to the API client instance it was fetched
 * with. If a different user signs in within the same tab (new client instance),
 * the cache is treated as stale so one user's workflow names never leak into
 * another's initial render.
 */
let workflowCache: WorkflowListItem[] | null = null;
let cachedForClient: unknown = null;

/** Drop the cached workflow list. Call on logout to avoid cross-session leakage. */
export const clearWorkflowListCache = (): void => {
  workflowCache = null;
  cachedForClient = null;
};

/**
 * useWorkflowList
 *
 * Fetches the workflows the current user can access via
 * `adminClient.workflows.listWorkflows`. The returned list is the access-scoped
 * source of truth (the backend only returns workflows the user may use), so it
 * doubles as the visibility gate for the command-menu Workflows section.
 *
 * Defensive by design: if the admin client (or its `listWorkflows` method) is
 * unavailable, the hook resolves to an empty list rather than throwing. This
 * keeps it safe in tests where the client is mocked without a `workflows` API.
 */
export const useWorkflowList = (): { workflows: WorkflowListItem[]; isLoading: boolean } => {
  const { adminClient } = useWebuildAPIClient();
  // Only seed from cache when it was fetched with the current client instance.
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>(() =>
    cachedForClient === adminClient ? (workflowCache ?? []) : [],
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const workflowsApi = adminClient?.workflows;
    const listWorkflows = workflowsApi?.listWorkflows;

    if (typeof listWorkflows !== "function") return;

    let cancelled = false;

    setIsLoading(true);
    // Promise.resolve().then(...) so a synchronous throw is funnelled into catch.
    Promise.resolve()
      .then(() => listWorkflows.call(workflowsApi, {}))
      .then(
        (
          result: Array<{
            id: string;
            name: string;
            description?: string | null;
            icon?: string | null;
          }>,
        ) => {
          if (cancelled) return;
          const mapped: WorkflowListItem[] = (result ?? []).map((w) => ({
            id: w.id,
            name: w.name,
            description: w.description ?? undefined,
            icon: w.icon ?? undefined,
          }));

          workflowCache = mapped;
          cachedForClient = adminClient;
          setWorkflows(mapped);
        },
      )
      .catch((error: unknown) => {
        logger.error("useWorkflowList: failed to fetch workflows", error);
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [adminClient]);

  return { workflows, isLoading };
};
